import React, { useMemo } from "react";
import PostCard from "../components/post-card";
import SearchBox from "../components/search-box";
import PaginatorLinks from "../components/paginator-links";
import { useQueryParam, StringParam, NumberParam } from "use-query-params";
import PostFilterStrip from "../components/post-filter-strip";
import algoliasearch from "algoliasearch/lite";
import { Helmet } from "react-helmet";
import {
  InstantSearch,
  Configure,
  ClearRefinements,
  Index,
  connectHits,
  connectSearchBox,
  connectPagination,
  connectMenu,
  connectStateResults,
} from "react-instantsearch-dom";

export default function CardSearch({ children, filters, pageSize = 6 }) {
  const [query] = useQueryParam("q", StringParam);
  const [tag] = useQueryParam("f", StringParam);
  const [pageParam] = useQueryParam("p", NumberParam);
  const page = pageParam || 0;

  const searchClient = useMemo(
    () =>
      algoliasearch(
        process.env.GATSBY_ALGOLIA_APP_ID,
        process.env.GATSBY_ALGOLIA_SEARCH_KEY
      ),
    []
  );

  const PostHits = connectHits(({ hits }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 md:px-8">
      {hits.map((hit) => (
        <PostCard
          className="h-auto"
          key={hit.objectID}
          post={{
            frontmatter: {
              title: hit.title,
              banner: hit.banner,
              type: hit.type,
              tags: hit.tags,
              categories: hit.category,
            },
            headings: [].concat(
              [{ value: hit.title }],
              hit.headings?.map((heading) => ({ value: heading }))
            ),
            fields: {
              slug: hit.slug,
            },
            excerpt: hit.excerpt,
          }}
        />
      ))}
    </div>
  ));

  const PostSearchBox = connectSearchBox(
    ({ currentRefinement, isSearchStalled, refine }) => (
      <SearchBox
        className="max-w-2xl"
        query={currentRefinement}
        onChange={(newQuery) => refine(newQuery)}
      />
    )
  );

  const PostPagination = connectPagination(
    ({ currentRefinement, nbPages, refine, createURL }) => (
      <PaginatorLinks
        current={currentRefinement - 1}
        max={nbPages}
        onChange={(index) => refine(index + 1)}
      />
    )
  );

  const PostFilter = connectMenu(({ items, currentRefinement, refine }) => (
    <PostFilterStrip
      categories={items.map((item) => item.label)}
      current={currentRefinement}
      onChange={(newTag) => {
        refine(items.find((item) => item.label === newTag)?.value);
      }}
    />
  ));

  const IndexSelector = connectStateResults(({ searchState, children }) =>
    searchState?.query ? (
      <Index indexName={process.env.GATSBY_ALGOLIA_INDEX}>{children}</Index>
    ) : (
      <Index indexName={`${process.env.GATSBY_ALGOLIA_INDEX}_date_desc`}>
        {children}
      </Index>
    )
  );

  const NoResultsText = connectStateResults(({ searchResults, children }) =>
    searchResults?.nbHits !== 0 ? (
      children
    ) : (
      <div className="text-center space-y-8">
        <p>No matching posts found!</p>
        <ClearRefinements
          translations={{
            reset: "Click here to clear all filters",
          }}
          clearsQuery
        />
      </div>
    )
  );

  return (
    <InstantSearch
      indexName={process.env.GATSBY_ALGOLIA_INDEX}
      searchClient={searchClient}
    >
      <Helmet>
        <link
          crossorigin
          href={`https://${process.env.GATSBY_ALGOLIA_APP_ID}-dsn.algolia.net`}
          rel="preconnect"
        />
      </Helmet>
      <Configure filters={filters} hitsPerPage={pageSize} distinct />

      {children}

      <div className="container w-full md:max-w-6xl mx-auto py-8 space-y-8">
        <PostFilter attribute="tags" limit={50} defaultRefinement={tag} />

        <div className="flex justify-center">
          <PostSearchBox defaultRefinement={query} />
        </div>

        <IndexSelector>
          <NoResultsText>
            <PostHits />
          </NoResultsText>
        </IndexSelector>

        <div className="w-full flex justify-center">
          <PostPagination defaultRefinement={page} />
        </div>
      </div>
    </InstantSearch>
  );
}
